import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle } from "lucide-react";
import { cdnImg } from "@/lib/api";
import { FilterBar } from "@/components/FilterBar";
import { sizeValue } from "@/lib/sizes";
import { colorFamily } from "@/lib/colors";
import { SITE } from "@/lib/seo";

const cleanTitle = (t) => (t || "").replace(/^\s*#?\d+\s*[-–:]\s*/, "");

/** Premium showcase for the $399 bridal & guest lehenga rail on /diwali-sale —
 *  larger photos, editorial framing, spotlight feel even with a single piece. */
export const BridalRail = ({ products }) => {
  const [sizes, setSizes] = useState([]);
  const [colors, setColors] = useState([]);
  const filtered = useMemo(() => {
    let l = products;
    if (sizes.length) l = l.filter((p) => (p.sizes || []).some((sz) => sizes.includes(sizeValue(sz))));
    if (colors.length) l = l.filter((p) => (p.colors || []).some((c) => colors.includes(colorFamily(c))));
    return l;
  }, [products, sizes, colors]);

  // Hidden until the Shopify collection exists and a sync has run.
  if (!products.length) return null;

  return (
    <section
      id="bridal"
      className="scroll-mt-24 border-t border-[#E5E5DF]"
      style={{ background: "linear-gradient(180deg, #FDF3EE 0%, #FAF9F6 70%)" }}
      data-testid="diwali-rail-bridal"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="overline text-[#C26A4A] mb-3">The Premium Edit · Flat $399</div>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight">Bridal &amp; Guest Lehengas</h2>
          <p className="text-sm sm:text-base text-[#6E6E69] mt-3 leading-relaxed">
            Heavier handwork pieces for the bride and her favourite people — every piece one flat price,
            tried on in-store or shipped across the USA.
          </p>
          <div className="text-[0.68rem] font-mono tracking-[0.16em] uppercase text-[#6E6E69] mt-4" data-testid="diwali-count-bridal">
            {filtered.length} {filtered.length === 1 ? "piece" : "pieces"}
          </div>
        </div>

        <FilterBar products={products} sizes={sizes} colors={colors} prices={[]} onSizes={setSizes} onColors={setColors} onPrices={() => {}} />

        {filtered.length === 0 ? (
          <div className="border border-dashed border-[#E7C9BC] bg-[#FFFFFF]/60 py-16 text-center" data-testid="diwali-empty-bridal">
            <p className="font-display text-2xl text-[#1A1A1A]">Nothing matches those filters</p>
            <p className="text-sm text-[#6E6E69] mt-2">Message us — new bridal pieces arrive through the season.</p>
            <a
              href={`https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent("Hi! I'm looking at the $399 bridal lehengas — can you help me find my size?")}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 mt-5 bg-[#25D366] text-[#0b3d1e] px-6 py-3 text-[0.7rem] font-mono tracking-[0.16em] uppercase hover:opacity-90 transition-opacity"
            >
              <MessageCircle size={14} /> Ask on WhatsApp
            </a>
          </div>
        ) : (
          <div className="flex flex-wrap justify-center gap-8 sm:gap-10">
            {filtered.map((p) => (
              <Link
                key={p.slug}
                to={`/product/${p.slug}`}
                data-testid={`bridal-card-${p.slug}`}
                className="group w-full sm:w-[21rem] lg:w-[23rem]"
              >
                <div className="border border-[#E7C9BC] bg-[#FFFFFF] p-3 group-hover:border-[#C26A4A] transition-colors duration-300 shadow-[0_18px_40px_-24px_rgba(194,106,74,0.35)]">
                  <div className="aspect-[3/4] overflow-hidden bg-[#EFEFEA]">
                    <img
                      src={cdnImg(p.images[0], 700)}
                      alt={cleanTitle(p.title)}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
                    />
                  </div>
                </div>
                <div className="mt-4 text-center">
                  <h3 className="font-display text-xl tracking-tight text-[#1A1A1A] leading-snug">{cleanTitle(p.title)}</h3>
                  <div className="flex items-center justify-center gap-3 mt-2">
                    <span className="inline-flex items-center bg-[#3D0A28] text-[#F5C542] text-[0.72rem] font-mono tracking-[0.1em] px-3 py-1" data-testid={`bridal-price-${p.slug}`}>
                      $399 flat
                    </span>
                    {(p.sizes || []).length > 0 && (
                      <span className="text-[0.68rem] font-mono tracking-[0.08em] uppercase text-[#6E6E69]">
                        {p.sizes.length === 1 ? `Size ${p.sizes[0]}` : `${p.sizes.length} sizes`}
                      </span>
                    )}
                  </div>
                  <span className="inline-flex items-center gap-1.5 mt-3 text-[0.68rem] font-mono tracking-[0.16em] uppercase text-[#C26A4A] group-hover:text-[#3D0A28] transition-colors">
                    View this piece <ArrowRight size={13} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}

        <p className="text-center text-[0.7rem] font-mono tracking-[0.14em] uppercase text-[#6E6E69] mt-12">
          One-of-a-kind pieces — once a piece sells, it&apos;s gone
        </p>
      </div>
    </section>
  );
};
